import { useMemo, useCallback } from "react"
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import {
  CURRENT_PROMOTION_FILTER_VALUE,
  Q_PROMOTION_GROUP,
  UPCOMING_PROMOTION_FILTER_VALUE,
} from "constant"

import { usePromotion } from "hooks/promotion/usePromotion"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { PromotionCard } from "components/domains/promotions"
import { EmptyData } from "components/EmptyData"
import { PercentIcon } from "components/icons"
import { LanguageButton } from "components/Language"
import { Carousel } from "components/Carousel/Carousel"
import { SearchToggleButton } from "components/domains/search"
import { CarouselItem } from "components/Carousel"
import { Head } from "components/Head"
import { VenueSelector } from "components/VenueSelector"
import { MotionDiv, showContent } from "components/motions"

import { getIcon } from "utils/icon"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"

export const PromotionsKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { openSearch } = useAppUI()
  const {
    changeVenueFilter,
    locationVenueId,
    venueFilter,
    groupPromotions,
    activeGroupPromotion,
    handleChange,
  } = usePromotion()
  const { venues, isMultiVenueProject } = useVenue()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { styleOverrides } = theme.components?.[`PromotionPage`] || {}
  const { promotionCarouselOption } =
    theme.components[`PromotionPage`]?.defaultProps || {}

  const {
    root,
    carouselContainer,
    carousel,
    emptyDataContainer,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionContainer,
    actionButton,
    searchBarContainer,
    promotionGroupSelectorButton,
    languageSelectMenu,
  } = styleOverrides
  const activePromotions = groupPromotions[activeGroupPromotion]
  const slidePerPage = 3
  const carouselType =
    activePromotions && activePromotions.length < slidePerPage
      ? "slide"
      : "loop"
  const autoplay = activePromotions && activePromotions.length > 3
  const focus =
    activePromotions.length < 3 ? "center" : promotionCarouselOption.focus

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  return (
    <>
      <Head title={t("Promotions")} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            animate="animate"
            initial="initial"
            variants={showContent}
            sx={carouselContainer}
          >
            {!activePromotions || activePromotions.length <= 0 ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay,
                  gap: "0",
                  ...promotionCarouselOption,
                  focus,
                }}
                sx={carousel}
              >
                {activePromotions.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/promotions/${promotion.id}?${Q_PROMOTION_GROUP}=${activeGroupPromotion}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            )}
            <Box sx={promotionGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupPromotion}
                onChange={handleChange}
                exclusive
              >
                <ToggleButton value={CURRENT_PROMOTION_FILTER_VALUE}>
                  {t("Current Promotions")}
                </ToggleButton>
                <ToggleButton value={UPCOMING_PROMOTION_FILTER_VALUE}>
                  {t("Upcoming Promotions")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
          </MotionDiv>
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
